import {makeAutoObservable, reaction, runInAction} from 'mobx';
import type {RootStore} from 'stores/rootStore';
import {Plan, PlanPeriod} from 'types';
import {getPlans, type OneTimeScanOffer} from 'requests';
import {detectClientCountry} from 'lib/clientCountry';
import {quotedHumanizerWords, type ServerArms} from 'utils/abTest';
import pricingDefaults from './pricingDefaults.json';


type PriceVariants = {monthly?: number; yearly?: number};
type PricingMap = {[key in PlanPeriod]: {[key in Plan]: {price: number}}};
type LimitsMap = {[key in Plan]: {[field: string]: any}};
type RawPlan = {key: string; prices?: PriceVariants; limits?: Record<string, any>};

const SUPPORTED_LIMIT_FIELDS = [
    "granted_words_simple_scan",
    "granted_words_humanizer",
    "deep_scans",
    "words_per_generation_limit",
    "batch_doc_upload",
    "document_upload_limit_mb",
    "history_retention_period_days",
    "extra_words_price",
    "extra_words_price__yearly",
] as const;

// The money among the limits is in the response's currency, like the prices.
// So when the backend omits one — this market has no amount for it — the
// bundled dollar default has to go with it: kept, it would be rendered under
// the market's symbol, which is the one thing pricing must never do.
const MONEY_LIMIT_FIELDS: string[] = ['extra_words_price', 'extra_words_price__yearly'];


function buildPricingDicts(plans: RawPlan[]): {PRICING: PricingMap; PLANS_LIMITS: LimitsMap} {
    const PRICING: any = {monthly: {}, yearly: {}};
    const PLANS_LIMITS: any = {};
    for (const plan of plans || []) {
        const k = plan.key;
        if (!k) continue;
        if (typeof plan.prices?.monthly === 'number') PRICING.monthly[k] = {price: plan.prices.monthly};
        if (typeof plan.prices?.yearly === 'number') PRICING.yearly[k] = {price: plan.prices.yearly};
        if (plan.limits) PLANS_LIMITS[k] = {...plan.limits};
    }
    return {PRICING, PLANS_LIMITS};
}

const DEFAULTS = buildPricingDicts(pricingDefaults.plans as RawPlan[]);

// Layers the live response on top of bundled defaults: prices and known limit fields
// from the response overlay defaults; unknown limit fields and missing plans keep defaults.
function mergeWithDefaults(livePlans: RawPlan[]): {PRICING: PricingMap; PLANS_LIMITS: LimitsMap} {
    if (!livePlans?.length) return DEFAULTS;
    const PRICING: any = {
        monthly: {...DEFAULTS.PRICING.monthly},
        yearly: {...DEFAULTS.PRICING.yearly},
    };
    const PLANS_LIMITS: any = {};
    for (const [k, v] of Object.entries(DEFAULTS.PLANS_LIMITS)) PLANS_LIMITS[k] = {...v};
    for (const plan of livePlans) {
        const k = plan.key;
        if (!k) continue;
        if (typeof plan.prices?.monthly === 'number') PRICING.monthly[k] = {price: plan.prices.monthly};
        if (typeof plan.prices?.yearly === 'number') PRICING.yearly[k] = {price: plan.prices.yearly};
        if (PLANS_LIMITS[k] && plan.limits) {
            for (const f of SUPPORTED_LIMIT_FIELDS) {
                if (f in plan.limits) PLANS_LIMITS[k][f] = plan.limits[f];
                else if (MONEY_LIMIT_FIELDS.includes(f)) delete PLANS_LIMITS[k][f];
            }
        }
    }
    return {PRICING, PLANS_LIMITS};
}


const DEFAULT_CURRENCY = 'usd';

export class PricingStore {
    root: RootStore;
    PRICING: PricingMap = DEFAULTS.PRICING;
    PLANS_LIMITS: LimitsMap = DEFAULTS.PLANS_LIMITS;
    // Currency the amounts above are in — always the backend's answer, so the
    // price on screen is the price Stripe will charge.
    currency: string = DEFAULT_CURRENCY;
    availableCurrencies: string[] = [];
    /** Unknown until the prices load — the offer is not made without a price to quote. */
    oneTimeScan: OneTimeScanOffer | null = null;
    /** Server-side A/B tests a signup can still be put in — none until the prices load. */
    experiments: ServerArms = {};

    constructor(root: RootStore) {
        this.root = root;
        // freeHumanizerWords reads the A/B cookie, which MobX can't observe: as a computed its
        // cached arm would outlive a cookie that changed under it.
        makeAutoObservable(this, {freeHumanizerWords: false});
        // Fire and forget — defaults stay in place if the request fails or is slow.
        this.refresh();
        // A country hint only helps the next fetch, so re-price once it lands
        // (and only if it actually changes anything).
        detectClientCountry().then(country => {
            if (country) this.refresh();
        });
        // Same for the session's token, which is minted after this store is built:
        // prices can depend on who is asking (an account pinned to a currency for
        // testing — see adminUtil's setCurrency), and a catalogue in one currency
        // beside a checkout in another is the one thing pricing must never do.
        reaction(() => this.root.user.token, token => {
            if (token) this.refresh();
        });
    }

    /** The free Humanizer words a sign-up surface quotes (the visitor's A/B arm while that test
     *  enrols). Reading it enrols the visitor, so only where the number is shown. */
    get freeHumanizerWords(): number {
        return quotedHumanizerWords(this.experiments, this.PLANS_LIMITS.free.granted_words_humanizer);
    }

    /** The one-time scan's price as its toolbar analytics events carry it: the price changes
     *  over time, and the funnel has to tell its cohorts apart. Null until the prices load. */
    get oneTimeScanPriceParams(): {price: number, currency: string} | null {
        const offer = this.oneTimeScan;
        return offer ? {price: offer.price, currency: offer.currency.toUpperCase()} : null;
    }

    /** The money fields of a one-time purchase event, from one snapshot of the offer. */
    oneTimeScanPurchaseParams(quantity: number): {value: number, quantity: number, price: number, currency: string} {
        const price = this.oneTimeScan?.price ?? 0;
        return {value: price * quantity, quantity, price, currency: (this.oneTimeScan?.currency || 'usd').toUpperCase()};
    }

    // Only currencies the backend offered can be picked: it re-checks anyway,
    // and letting the UI ask for others would just show prices we can't honour.
    get canSwitchCurrency(): boolean {
        return this.availableCurrencies.length > 1;
    }

    async selectCurrency(currency: string) {
        if (currency === this.currency) return;
        await this.refresh(currency);
    }

    async refresh(currency?: string) {
        try {
            const {plans, currency: resolved, availableCurrencies, oneTimeScan, experiments} = await getPlans(currency);
            const result = mergeWithDefaults(plans);
            runInAction(() => {
                this.PRICING = result.PRICING;
                this.PLANS_LIMITS = result.PLANS_LIMITS;
                // Trust the response, not the request: asking for a currency we
                // aren't entitled to comes back as the one we actually get.
                this.currency = resolved || DEFAULT_CURRENCY;
                this.availableCurrencies = availableCurrencies || [];
                this.oneTimeScan = oneTimeScan ?? null;
                this.experiments = experiments ?? {};
            });
        } catch {
            // keep defaults
        }
    }
}
